import { DirectSale } from '@/core/domain/sales/DirectSale/DirectSale.types';
import { Button, FormInstance, Modal } from 'antd';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import DirectSaleGenerateInvoiceForm from '../../DirectSaleGenerateInvoiceForm';
import moment from 'moment';
import { DateTimeFormat } from '@/utils/moment';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import {
  useInvoiceCreateNFc,
  useInvoiceCreateNFe,
} from '@/core/domain/invoice/invoices/invoices.hooks';
import {
  InvoiceCreateNFc,
  InvoiceCreateNFe,
} from '@/core/domain/invoice/invoices/invoices.createTypes';
import {
  InvoiceDocumentType,
  InvoiceEmissionType,
  InvoiceItemType,
  InvoiceOperationDestination,
  InvoiceOriginReferenceEntity,
  InvoiceRecipientType,
  InvoiceTaxpayerType,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { PersonType } from '@/core/domain/people/Customers/Customers.types';
import { useCustomerGeneralMutation } from '@/core/domain/people/Customers/Customers.hooks';

interface DirectSaleGenerateInvoiceFormValues {
  companyId: number;
  invoiceType: InvoiceType;
}
interface DirectSaleGenerateInvoiceModalProps {
  form: FormInstance<DirectSaleGenerateInvoiceFormValues>;
  visible: boolean;
  onCancel: () => void;
  directSale?: DirectSale;
}

export default function DirectSaleGenerateInvoiceModal({
  form,
  visible,
  onCancel,
  directSale,
}: DirectSaleGenerateInvoiceModalProps) {
  const intl = useIntl();
  const history = useHistory();

  const invoceSettingsByCompany = useInvoiceSettingsByCompany(
    directSale?.companyId,
    visible,
  );

  const customerGeneral = useCustomerGeneralMutation();

  const generateInvoiceNFcMutation = useInvoiceCreateNFc();
  const generateInvoiceNFeMutation = useInvoiceCreateNFe();

  const isLoading =
    generateInvoiceNFcMutation.isLoading ||
    generateInvoiceNFeMutation.isLoading;

  const handleGenerateInvoice = async (generateRedirect?: boolean) => {
    if (directSale) {
      directSale.companyId;
      const values = await form.validateFields();
      const resultCompany = invoceSettingsByCompany.data;

      let finalConsumer: boolean | undefined;

      const customer = await customerGeneral.mutateAsync(
        directSale?.customerId,
      );

      if (values.invoiceType === InvoiceType.NFE) {
        finalConsumer = customer?.taxpayerType
          ? customer?.taxpayerType === InvoiceTaxpayerType.NON_CONTRIBUTOR
          : customer.personType === PersonType.Natural;
      }

      const obj: InvoiceCreateNFc | InvoiceCreateNFe = {
        isIrpjCsllRetention: customer?.retentionIRPJorCSLL,
        customerId: directSale.customerId,
        emissionDate: moment().format(DateTimeFormat),
        emissionType: InvoiceEmissionType.NORMAL_EMISSION,
        documentType: InvoiceDocumentType.EXIT,
        serie:
          values.invoiceType === InvoiceType.NFE
            ? resultCompany?.configNfe?.series
            : resultCompany?.configNfc?.series,
        taxpayerType: InvoiceTaxpayerType.NON_CONTRIBUTOR,
        recipientType: InvoiceRecipientType.CUSTOMER,
        companyId: values.companyId,
        operationDestination: InvoiceOperationDestination.INTERNAL_OPERATION,
        finalConsumer,
        faceToFace: true,
        companyAdditionalData:
          values.invoiceType === InvoiceType.NFE
            ? resultCompany?.configNfe?.companyAdditionalDataDefault
            : resultCompany?.configNfs?.companyAdditionalDataDefault,
        taxAdditionalData:
          values.invoiceType === InvoiceType.NFE
            ? resultCompany?.configNfe?.taxAdditionalDataDefault
            : undefined,
        invoiceItem: directSale.items.map((item) => ({
          type: InvoiceItemType.PART,
          valueDiscount: item.discountCurrency || 0,
          valueUnitary: item.unitPrice,
          quantity: item.quantity,
          partId: item.part.id,
          description: item.part.name,
          saleItemId: item.id,
        })),
        originReference: [
          {
            entity: InvoiceOriginReferenceEntity.SALES,
            entityId: directSale.id,
            entityCode: directSale.saleCode,
          },
        ],
        invoiceType: values.invoiceType,
      };

      const generateInvoiceMutation =
        values.invoiceType === InvoiceType.NFC
          ? generateInvoiceNFcMutation
          : generateInvoiceNFeMutation;

      const result = await generateInvoiceMutation.mutateAsync(obj);

      if (generateRedirect) {
        return history.push(
          `/app/fiscal/invoice/${values.invoiceType}/${result.id}/datas`,
        );
      }

      history.push(`/app/movements/directSale/${directSale.id}`);
    }
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'directSale.button.generateInvoice' })}
      open={visible}
      onCancel={onCancel}
      width={800}
      destroyOnClose
      footer={
        <SpaceForButton justifyContent="end">
          <Button block onClick={onCancel}>
            {intl.formatMessage({ id: 'commons.modal.button.cancel' })}
          </Button>

          <Button
            block
            type="primary"
            onClick={() => handleGenerateInvoice(false)}
            loading={isLoading}
          >
            {intl.formatMessage({
              id: 'directSale.generateInvoice.modal.create',
            })}
          </Button>

          <Button
            block
            type="primary"
            onClick={() => handleGenerateInvoice(true)}
            loading={isLoading}
          >
            {intl.formatMessage({
              id: 'directSale.generateInvoice.modal.createRedirect',
            })}
          </Button>
        </SpaceForButton>
      }
    >
      <DirectSaleGenerateInvoiceForm form={form} />
    </Modal>
  );
}
